import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Paintbrush, Pencil, Eraser, Pipette, PaintBucket, Layers,
  Plus, Trash2, Eye, EyeOff, Wand2, Image as ImageIcon,
  Download, Undo, Redo, RotateCcw, Sparkles, Move, Type, Sliders, X,
  ZoomIn, ZoomOut, History
} from 'lucide-react';
import { NetworkManager } from '../../game/NetworkManager';

interface LayerData {
  id: string;
  name: string;
  visible: boolean;
  opacity: number; // 0 to 1
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

interface DrawTogetherProps {
  /**
   * `undefined` (omitted) defaults to the shared 'vc' room — the
   * multiplayer path. Pass `null` explicitly to opt out of networking
   * entirely (solo mode): a default parameter only applies when the
   * argument is `undefined`, so `null` is the only value that reaches
   * the `if (!roomId) return;` guard below without being overwritten.
   */
  roomId?: string | null;
  currentUserId?: string;
  currentUsername?: string;
  /**
   * GameManager mounts every registered game component with this prop
   * (see GameManager.tsx's `<GameComponent onExit={handleExitGame} ...>`)
   * so the picker/lobby can reclaim the activity surface and App.tsx's
   * `activeGame` can clear (which is what lets the 🎮 broadcast decay for
   * peers). Without wiring it there was no way to leave this activity —
   * `activeGame` stayed set forever, permanently showing HOST ONLY for
   * every other voice-channel participant.
   */
  onExit?: () => void;
}

type ToolType = 'pen' | 'chisel' | 'eraser' | 'bucket' | 'eyedropper' | 'wand' | 'select';

// Magic Wand selection: finds contiguous pixels of similar color and returns their positions
function magicWandSelect(ctx: CanvasRenderingContext2D, startX: number, startY: number, width: number, height: number, tolerance: number = 30): Set<string> {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  const getPixel = (x: number, y: number): [number, number, number, number] => {
    const idx = (Math.floor(y) * width + Math.floor(x)) * 4;
    return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
  };

  const colorDistance = (c1: [number, number, number, number], c2: [number, number, number, number]): number => {
    const [r1, g1, b1, a1] = c1;
    const [r2, g2, b2, a2] = c2;
    return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2 + (a1 - a2) ** 2);
  };

  const target = getPixel(startX, startY);
  const selected = new Set<string>();
  const queue: Array<[number, number]> = [[startX, startY]];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const [x, y] = queue.shift()!;
    const key = `${x},${y}`;
    if (visited.has(key) || x < 0 || x >= width || y < 0 || y >= height) continue;
    visited.add(key);

    const pixel = getPixel(x, y);
    if (colorDistance(pixel, target) <= tolerance) {
      selected.add(key);
      queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
  }

  return selected;
}

// Draw marching ants selection border
function drawSelectionBorder(ctx: CanvasRenderingContext2D, selectedPixels: Set<string>, offset: number = 0) {
  ctx.strokeStyle = '#00ff00';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.lineDashOffset = offset;

  selectedPixels.forEach(key => {
    const [x, y] = key.split(',').map(Number);
    ctx.strokeRect(x - 0.5, y - 0.5, 1, 1);
  });

  ctx.setLineDash([]);
}

// Flood-fill algorithm: fills a contiguous region of similar color with fillColor
function floodFill(ctx: CanvasRenderingContext2D, startX: number, startY: number, fillColor: string, width: number, height: number) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  const getPixel = (x: number, y: number): [number, number, number, number] => {
    const idx = (Math.floor(y) * width + Math.floor(x)) * 4;
    return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
  };

  const setPixel = (x: number, y: number) => {
    const idx = (Math.floor(y) * width + Math.floor(x)) * 4;
    const hex = fillColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    data[idx] = r;
    data[idx + 1] = g;
    data[idx + 2] = b;
    data[idx + 3] = 255;
  };

  const target = getPixel(startX, startY);
  const queue: Array<[number, number]> = [[startX, startY]];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const [x, y] = queue.shift()!;
    const key = `${x},${y}`;
    if (visited.has(key) || x < 0 || x >= width || y < 0 || y >= height) continue;
    visited.add(key);

    const pixel = getPixel(x, y);
    if (pixel[0] !== target[0] || pixel[1] !== target[1] || pixel[2] !== target[2] || pixel[3] !== target[3]) continue;

    setPixel(x, y);
    queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }

  ctx.putImageData(imageData, 0, 0);
}

export default function DrawTogetherActivity({
  roomId = 'vc',
  currentUserId = 'me',
  currentUsername = 'Artist',
  onExit,
}: DrawTogetherProps) {
  // Canvas viewport dimensions. Bumped 1280x720 -> 1920x1080 (same 16:9)
  // so there are meaningfully more pixels to zoom into — at the old size a
  // 4x zoom just magnified soft pixels rather than revealing detail.
  const CANVAS_WIDTH = 1920;
  const CANVAS_HEIGHT = 1080;
  const MAX_HISTORY = 40;

  // Composite Canvas Ref for display
  const mainCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  // Layer state
  const [layers, setLayers] = useState<LayerData[]>([]);
  const [activeLayerId, setActiveLayerId] = useState<string>('');
  
  // Drawing Tools State
  const [activeTool, setActiveTool] = useState<ToolType>('chisel');
  const [brushColor, setBrushColor] = useState<string>('#38bdf8');
  const [brushSize, setBrushSize] = useState<number>(16);
  const [brushOpacity, setBrushOpacity] = useState<number>(1);
  const [chiselAngle, setChiselAngle] = useState<number>(45); // Degrees for Chisel Nib
  // Canvas view zoom — the composite canvas is a fixed 1280x720 buffer
  // squeezed into whatever space is left of the call's activity surface
  // (sidebar + control bar reserve it), so there was previously no way to
  // see fine detail. Scales the display container only; getPos() below
  // reads getBoundingClientRect() so pointer-to-canvas-pixel mapping stays
  // correct at any zoom level.
  const [zoom, setZoom] = useState<number>(1);
  
  // Interaction State
  const [isDrawing, setIsDrawing] = useState(false);
  const [showLayerPanel, setShowLayerPanel] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const selectedPixelsRef = useRef<Set<string>>(new Set());
  const [selectionAnimOffset, setSelectionAnimOffset] = useState(0);

  // ---- Undo / redo -------------------------------------------------------
  // Snapshot-based: before any mutation we capture the affected layer's
  // pixels. Storing per-layer ImageData (rather than the whole composite)
  // keeps each entry to one layer's worth of memory, and restoring is a
  // single putImageData. Depth is capped at MAX_HISTORY so a long session
  // can't grow unbounded.
  //
  // Refs, not state: the keydown handler and the draw handlers both need
  // the live stacks without re-subscribing/re-rendering on every stroke.
  // `historyVersion` exists purely to re-render the toolbar's enabled state.
  type HistoryEntry = { layerId: string; data: ImageData };
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const snapshotLayer = (layerId: string): HistoryEntry | null => {
    const layer = layersRef.current.find(l => l.id === layerId);
    if (!layer) return null;
    return {
      layerId,
      data: layer.ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT),
    };
  };

  // Throttle stroke broadcasts to ~60fps to reduce network traffic
  const lastStrokeBroadcastRef = useRef<number>(0);
  const throttleBroadcastStroke = (strokeData: any) => {
    const now = Date.now();
    if (now - lastStrokeBroadcastRef.current > 16) { // ~60fps throttle
      lastStrokeBroadcastRef.current = now;
      if (networkRef.current) {
        if (import.meta.env.DEV) console.log(`[DrawTogether] Broadcasting ${strokeData.tool} stroke`);
        networkRef.current.sendInput({
          event: 'draw-stroke',
          payload: strokeData,
        } as any);
      }
    }
  };

  /** Capture `layerId`'s current pixels as an undo point. Clears redo. */
  const pushHistory = (layerId: string) => {
    const entry = snapshotLayer(layerId);
    if (!entry) return;
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > MAX_HISTORY) undoStackRef.current.shift();
    // Any new edit invalidates the redo branch, same as every paint app.
    redoStackRef.current = [];
    setHistoryVersion(v => v + 1);
  };

  const undo = () => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    const current = snapshotLayer(entry.layerId);
    if (current) redoStackRef.current.push(current);
    const layer = layersRef.current.find(l => l.id === entry.layerId);
    if (layer) layer.ctx.putImageData(entry.data, 0, 0);
    renderComposite();
    setHistoryVersion(v => v + 1);
  };

  const redo = () => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    const current = snapshotLayer(entry.layerId);
    if (current) undoStackRef.current.push(current);
    const layer = layersRef.current.find(l => l.id === entry.layerId);
    if (layer) layer.ctx.putImageData(entry.data, 0, 0);
    renderComposite();
    setHistoryVersion(v => v + 1);
  };

  // Sync Channel Ref
  const networkRef = useRef<NetworkManager | null>(null);
  const layersRef = useRef<LayerData[]>([]);
  layersRef.current = layers;

  // Initialize Layer Engine on Mount
  useEffect(() => {
    // Create initial Background layer only (keep it simple)
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = CANVAS_WIDTH;
    bgCanvas.height = CANVAS_HEIGHT;
    const bgCtx = bgCanvas.getContext('2d')!;
    bgCtx.fillStyle = '#090d16';
    bgCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const bgLayer: LayerData = {
      id: 'bg-layer',
      name: 'Background',
      visible: true,
      opacity: 1,
      canvas: bgCanvas,
      ctx: bgCtx,
    };

    setLayers([bgLayer]);
    setActiveLayerId('bg-layer');
  }, []);

  // Re-composite layers onto display canvas whenever layers change or drawing occurs
  const renderComposite = () => {
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) return;
    const ctx = mainCanvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    layers.forEach(layer => {
      if (layer.visible) {
        ctx.globalAlpha = layer.opacity;
        ctx.drawImage(layer.canvas, 0, 0);
      }
    });
    ctx.globalAlpha = 1.0;

    // Draw selection border (marching ants) if selection exists
    if (selectedPixelsRef.current.size > 0) {
      drawSelectionBorder(ctx, selectedPixelsRef.current, selectionAnimOffset);
    }
  };

  useEffect(() => {
    renderComposite();
  }, [layers]);

  // Animate marching ants selection border
  useEffect(() => {
    if (selectedPixelsRef.current.size === 0) return;

    const interval = setInterval(() => {
      setSelectionAnimOffset(prev => (prev + 2) % 8);
      renderComposite();
    }, 50);

    return () => clearInterval(interval);
  }, [selectedPixelsRef.current.size]);

  // Real-time Sync Setup via NetworkManager
  useEffect(() => {
    if (!roomId) return;
    const nm = new NetworkManager(roomId, 'draw-together');

    nm.onPlayerUpdate = (data: any) => {
      // NetworkManager.onPlayerUpdate is typed as PlayerState but the
      // wire envelope we receive here { event: string, payload?: any }
      // is a union shape. Cast to any locally — the event handler is the
      // only consumer of these properties, and the runtime narrowing on
      // `data.event` / `data.payload` is the actual contract.
      if (data.event === 'draw-stroke' && data.payload) {
        handleRemoteStroke(data.payload);
      } else if (data.event === 'fill' && data.payload) {
        const { layerId, x, y, color } = data.payload;
        const targetLayer = layersRef.current.find(l => l.id === layerId);
        if (targetLayer) {
          floodFill(targetLayer.ctx, x, y, color, CANVAS_WIDTH, CANVAS_HEIGHT);
          renderComposite();
        }
      } else if (data.event === 'clear-layer' && data.payload) {
        if (data.payload.layerId) {
          clearLayerContent(data.payload.layerId);
        }
      } else if (data.event === 'full-canvas-sync' && data.payload) {
        if (data.payload.layers) {
          data.payload.layers.forEach((syncedLayer: any) => {
            const img = new Image();
            img.onload = () => {
              const layer = layersRef.current.find(l => l.id === syncedLayer.id);
              if (layer) {
                layer.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                layer.ctx.drawImage(img, 0, 0);
                renderComposite();
                if (import.meta.env.DEV) console.log(`[DrawTogether] Synced layer ${syncedLayer.id}`);
              }
            };
            img.onerror = () => {
              console.error(`[DrawTogether] Failed to sync layer ${syncedLayer.id}`);
            };
            img.src = syncedLayer.dataUrl;
          });
        }
      }
    };

    nm.onPlayerList = (players) => {
      setIsHost(nm.isHost);
      if (nm.isHost) {
        const layerData = layersRef.current.map(l => ({
          id: l.id,
          dataUrl: l.canvas.toDataURL()
        }));
        nm.sendInput({
          event: 'full-canvas-sync',
          payload: { layers: layerData }
        } as any);
      }
    };

    networkRef.current = nm;

    return () => {
      nm.socket.disconnect();
      networkRef.current = null;
      setIsHost(false);
    };
  }, [roomId]); // layers removed from dependency to avoid reconnecting

  // Helper: Broadcast stroke to peers
  const broadcastStroke = (strokeData: any) => {
    if (networkRef.current) {
      networkRef.current.sendInput({
        event: 'draw-stroke',
        payload: strokeData,
      } as any);
    }
  };

  const broadcastClearLayer = (layerId: string) => {
    clearLayerContent(layerId);
    if (networkRef.current) {
      if (import.meta.env.DEV) console.log(`[DrawTogether] Broadcasting clear layer ${layerId}`);
      networkRef.current.sendInput({
        event: 'clear-layer',
        payload: { layerId },
      } as any);
    }
  };

  // Helper: Draw Chisel Brush Line (angled nib polygon)
  const drawChiselLine = (
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number,
    x2: number, y2: number,
    color: string, size: number, angleDeg: number, opacity: number
  ) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = opacity;

    const rad = (angleDeg * Math.PI) / 180;
    const dx = (size / 2) * Math.cos(rad);
    const dy = (size / 2) * Math.sin(rad);

    const dist = Math.hypot(x2 - x1, y2 - y1);
    const steps = Math.max(Math.ceil(dist / 2), 1);

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const cx = x1 + (x2 - x1) * t;
      const cy = y1 + (y2 - y1) * t;

      ctx.beginPath();
      ctx.moveTo(cx - dx, cy - dy);
      ctx.lineTo(cx + dx, cy + dy);
      ctx.lineTo(cx + dx + 1, cy + dy + 1);
      ctx.lineTo(cx - dx + 1, cy - dy + 1);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  };

  // Helper: Draw Standard Round Line
  const drawRoundLine = (
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number,
    x2: number, y2: number,
    color: string, size: number, opacity: number, isEraser = false
  ) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    if (isEraser) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.restore();
  };

  // Handle Remote Strokes
  const handleRemoteStroke = (stroke: any) => {
    const targetLayer = layersRef.current.find(l => l.id === stroke.layerId) || layersRef.current.find(l => l.id !== 'bg-layer');
    if (!targetLayer) {
      if (import.meta.env.DEV) console.warn(`[DrawTogether] Layer not found for stroke`, stroke);
      return;
    }

    if (stroke.tool === 'chisel') {
      drawChiselLine(targetLayer.ctx, stroke.x1, stroke.y1, stroke.x2, stroke.y2, stroke.color, stroke.size, stroke.angle, stroke.opacity);
    } else if (stroke.tool === 'pen' || stroke.tool === 'eraser') {
      drawRoundLine(targetLayer.ctx, stroke.x1, stroke.y1, stroke.x2, stroke.y2, stroke.color, stroke.size, stroke.opacity, stroke.tool === 'eraser');
    }
    renderComposite();
    if (import.meta.env.DEV) console.log(`[DrawTogether] Applied ${stroke.tool} stroke on layer ${stroke.layerId}`);
  };

  // Mouse / Touch Event Handlers
  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handleStartDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const pos = getCanvasCoords(e);
    setIsDrawing(true);
    lastPosRef.current = pos;

    const activeLayer = layers.find(l => l.id === activeLayerId);
    if (!activeLayer) return;

    if (activeTool === 'eyedropper') {
      // Pick color from composite
      const mainCanvas = mainCanvasRef.current;
      if (mainCanvas) {
        const ctx = mainCanvas.getContext('2d');
        if (ctx) {
          try {
            const pixel = ctx.getImageData(Math.round(pos.x), Math.round(pos.y), 1, 1).data;
            const hex = `#${((1 << 24) + (pixel[0] << 16) + (pixel[1] << 8) + pixel[2]).toString(16).slice(1)}`;
            setBrushColor(hex);
            setActiveTool('chisel');
          } catch (e) {
            // Canvas tainted by cross-origin pasted image
            console.error('DrawTogether: eyedropper failed (tainted canvas)', e);
          }
        }
      }
      setIsDrawing(false);
      return;
    }

    if (activeTool === 'bucket') {
      // Flood Fill Active Layer
      pushHistory(activeLayer.id);
      floodFill(activeLayer.ctx, pos.x, pos.y, brushColor, CANVAS_WIDTH, CANVAS_HEIGHT);
      renderComposite();
      // Broadcast fill to peers
      broadcastStroke({
        event: 'fill',
        layerId: activeLayer.id,
        x: pos.x,
        y: pos.y,
        color: brushColor,
      });
      setIsDrawing(false);
      return;
    }

    if (activeTool === 'wand') {
      // Magic Wand selection
      const selected = magicWandSelect(activeLayer.ctx, pos.x, pos.y, CANVAS_WIDTH, CANVAS_HEIGHT);
      selectedPixelsRef.current = selected;
      setSelectionAnimOffset(0);
      console.log(`Selected ${selected.size} pixels with magic wand`);
      setIsDrawing(false);
      return;
    }

    // One undo point per stroke: captured here, before the first dab, so
    // an undo rewinds the whole drag rather than a single segment of it.
    pushHistory(activeLayer.id);

    // Draw initial point (single-click dot)
    if (activeTool === 'chisel') {
      drawChiselLine(activeLayer.ctx, pos.x, pos.y, pos.x + 0.1, pos.y + 0.1, brushColor, brushSize, chiselAngle, brushOpacity);
    } else if (activeTool === 'pen' || activeTool === 'eraser') {
      drawRoundLine(activeLayer.ctx, pos.x, pos.y, pos.x + 0.1, pos.y + 0.1, brushColor, brushSize, brushOpacity, activeTool === 'eraser');
    }

    // Broadcast initial point immediately so single-click dots appear on peers
    // (eyedropper/bucket/wand already returned above, so only select needs excluding)
    if (activeTool !== 'select') {
      broadcastStroke({
        layerId: activeLayer.id,
        tool: activeTool,
        x1: pos.x,
        y1: pos.y,
        x2: pos.x + 0.1,
        y2: pos.y + 0.1,
        color: activeTool === 'eraser' ? '#000' : brushColor,
        size: brushSize,
        angle: chiselAngle,
        opacity: activeTool === 'eraser' ? 1.0 : brushOpacity,
      });
    }

    renderComposite();
  };

  const handleMoveDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !lastPosRef.current) return;
    const pos = getCanvasCoords(e);
    const activeLayer = layers.find(l => l.id === activeLayerId);
    if (!activeLayer) return;

    if (activeTool !== 'eyedropper' && activeTool !== 'bucket' && activeTool !== 'wand' && activeTool !== 'select') {
      throttleBroadcastStroke({
        layerId: activeLayer.id,
        tool: activeTool,
        x1: lastPosRef.current.x,
        y1: lastPosRef.current.y,
        x2: pos.x,
        y2: pos.y,
        color: activeTool === 'eraser' ? '#000' : brushColor,
        size: brushSize,
        angle: chiselAngle,
        opacity: activeTool === 'eraser' ? 1.0 : brushOpacity
      });
    }

    if (activeTool === 'chisel') {
      drawChiselLine(activeLayer.ctx, pos.x, pos.y, lastPosRef.current.x, lastPosRef.current.y, brushColor, brushSize, chiselAngle, brushOpacity);
    } else if (activeTool === 'pen' || activeTool === 'eraser') {
      drawRoundLine(activeLayer.ctx, lastPosRef.current.x, lastPosRef.current.y, pos.x, pos.y, brushColor, brushSize, brushOpacity, activeTool === 'eraser');
    }

    lastPosRef.current = pos;
    renderComposite();
  };

  const handleEndDraw = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  // Image Paste Handler (Clipboard & Drag-and-Drop)
  const handlePasteImage = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (!blob) continue;
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            // Create a new layer for pasted image
            const newCanvas = document.createElement('canvas');
            newCanvas.width = CANVAS_WIDTH;
            newCanvas.height = CANVAS_HEIGHT;
            const newCtx = newCanvas.getContext('2d')!;
            
            // Draw image centered
            const x = (CANVAS_WIDTH - img.width) / 2;
            const y = (CANVAS_HEIGHT - img.height) / 2;
            newCtx.drawImage(img, x, y);

            const newLayer: LayerData = {
              id: `pasted-${Date.now()}`,
              name: `Pasted Image ${layers.length}`,
              visible: true,
              opacity: 1,
              canvas: newCanvas,
              ctx: newCtx,
            };

            setLayers(prev => [...prev, newLayer]);
            setActiveLayerId(newLayer.id);
          };
          img.src = event.target?.result as string;
        };
        reader.readAsDataURL(blob);
      }
    }
  };

  // Layer Management Functions
  const addLayer = () => {
    const newCanvas = document.createElement('canvas');
    newCanvas.width = CANVAS_WIDTH;
    newCanvas.height = CANVAS_HEIGHT;
    const newCtx = newCanvas.getContext('2d')!;

    const newLayer: LayerData = {
      id: `layer-${Date.now()}`,
      name: `Layer ${layers.length}`,
      visible: true,
      opacity: 1,
      canvas: newCanvas,
      ctx: newCtx,
    };

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newLayer.id);
  };

  const deleteLayer = (id: string) => {
    if (layers.length <= 1 || id === 'bg-layer') return;
    setLayers(prev => prev.filter(l => l.id !== id));
    if (activeLayerId === id) {
      const remaining = layers.filter(l => l.id !== id);
      setActiveLayerId(remaining[remaining.length - 1]?.id || 'bg-layer');
    }
  };

  const toggleLayerVisibility = (id: string) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, visible: !l.visible } : l));
  };

  const updateLayerOpacity = (id: string, opacity: number) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, opacity } : l));
  };

  const clearLayerContent = (id: string) => {
    const layer = layers.find(l => l.id === id);
    if (!layer) return;
    pushHistory(id);
    if (id === 'bg-layer') {
      layer.ctx.fillStyle = '#090d16';
      layer.ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    } else {
      layer.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }
    renderComposite();
  };

  // ---- Canvas archive (last 5 drawings per voice channel) ----------------
  // Undo history is per-session and dies with the component, so exiting the
  // activity — or hitting Reset — used to lose the drawing outright. The
  // archive snapshots the flattened composite to localStorage, keyed by
  // room, so previous drawings can be restored after the fact.
  const ARCHIVE_LIMIT = 5;
  const archiveKey = `drawtogether_archive_${roomId ?? 'solo'}`;

  const [archive, setArchive] = useState<{ at: number; data: string }[]>([]);
  const [showArchive, setShowArchive] = useState(false);

  const readArchive = (): { at: number; data: string }[] => {
    try {
      const raw = localStorage.getItem(archiveKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    setArchive(readArchive());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveKey]);

  /** Snapshot the current composite to the front of the archive. */
  const archiveSnapshot = () => {
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) return;
    try {
      // JPEG at 0.6 keeps a 1920x1080 frame near ~100KB so five of them fit
      // comfortably inside a typical 5MB localStorage budget; PNG at this
      // size blows the quota after two or three saves.
      const data = mainCanvas.toDataURL('image/jpeg', 0.6);
      const next = [{ at: Date.now(), data }, ...readArchive()].slice(0, ARCHIVE_LIMIT);
      localStorage.setItem(archiveKey, JSON.stringify(next));
      setArchive(next);
    } catch (e) {
      // Quota exceeded / canvas tainted by a cross-origin pasted image.
      console.error('DrawTogether: could not archive canvas', e);
    }
  };

  /** Paint an archived snapshot back onto the active layer. */
  const restoreSnapshot = (dataUrl: string) => {
    const layer = layersRef.current.find(l => l.id === activeLayerId);
    if (!layer) return;
    const img = new Image();
    img.onload = () => {
      pushHistory(layer.id);
      layer.ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      renderComposite();
      setShowArchive(false);
    };
    img.src = dataUrl;
  };

  // Autosave every 2 minutes while there is anything in the undo stack (i.e.
  // the user has actually drawn), plus once on unmount, so leaving the
  // activity never silently discards work.
  useEffect(() => {
    const id = setInterval(() => {
      if (undoStackRef.current.length > 0) archiveSnapshot();
    }, 120000);
    return () => {
      clearInterval(id);
      if (undoStackRef.current.length > 0) archiveSnapshot();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveKey]);

  /**
   * Wipe every layer back to a blank board. Confirmed because it is not
   * recoverable in one undo step (it spans all layers, and history entries
   * are per-layer).
   */
  const resetBoard = () => {
    if (!window.confirm('Reset the board? This clears every layer.')) return;
    // Archive before wiping so a mis-click is recoverable from History.
    archiveSnapshot();
    layersRef.current.forEach(layer => {
      if (layer.id === 'bg-layer') {
        layer.ctx.fillStyle = '#090d16';
        layer.ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      } else {
        layer.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }
    });
    undoStackRef.current = [];
    redoStackRef.current = [];
    setHistoryVersion(v => v + 1);
    renderComposite();
  };

  // Ctrl+Z / Ctrl+Y (and Ctrl+Shift+Z, the other common redo binding).
  // Bound on window so the shortcuts work regardless of whether the canvas
  // wrapper currently holds focus, but skipped while the user is typing in
  // an input so we never hijack text editing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCanvasImage = () => {
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) return;
    const link = document.createElement('a');
    link.download = `DrawTogether_${Date.now()}.png`;
    link.href = mainCanvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div 
      tabIndex={0}
      onPaste={handlePasteImage}
      className="w-full h-full bg-[#080b14] rounded-3xl border border-white/10 flex flex-col overflow-hidden relative select-none outline-none"
    >
      {/* Header Toolbar */}
      <div className="px-6 py-3 border-b border-white/10 bg-black/40 backdrop-blur-xl flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-black shadow-lg">
            <Paintbrush size={18} />
          </div>
          <div>
            <h2 className="text-white font-black text-xs uppercase tracking-widest flex items-center gap-2">
              DrawTogether <span className="px-2 py-0.5 rounded-full bg-[var(--neon)]/10 border border-[var(--neon)]/20 text-[9px] text-[var(--neon)] font-mono">Paint.NET VC Studio</span>
              {isHost && <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[9px] text-amber-400 font-mono">HOST</span>}
            </h2>
            <p className="text-[10px] text-white/40 font-mono mt-0.5">Shared Canvas • Real-Time Sync • {isHost ? 'Hosting Session' : 'Connected'}</p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLayerPanel(!showLayerPanel)}
            className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all ${showLayerPanel ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30 text-[var(--neon)]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}
          >
            <Layers size={14} /> Layers ({layers.length})
          </button>

          <button
            onClick={undo}
            disabled={undoStackRef.current.length === 0}
            title="Undo (Ctrl+Z)"
            className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Undo size={14} /> Undo
          </button>

          <button
            onClick={redo}
            disabled={redoStackRef.current.length === 0}
            title="Redo (Ctrl+Y)"
            className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Redo size={14} /> Redo
          </button>

          <button
            onClick={resetBoard}
            title="Clear every layer"
            className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all"
          >
            <RotateCcw size={14} /> Reset
          </button>

          <button
            onClick={() => { setArchive(readArchive()); setShowArchive(v => !v); }}
            title="Recent drawings in this channel (autosaved)"
            className={`px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all ${
              showArchive
                ? 'bg-[var(--neon)]/10 border-[var(--neon)]/30 text-[var(--neon)]'
                : 'bg-white/5 border-white/10 hover:bg-white/10 text-white'
            }`}
          >
            <History size={14} /> History{archive.length > 0 ? ` (${archive.length})` : ''}
          </button>

          <button
            onClick={exportCanvasImage}
            className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all"
          >
            <Download size={14} /> Export PNG
          </button>

          {onExit && (
            <button
              onClick={onExit}
              className="px-3 py-1.5 rounded-xl bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all"
            >
              <X size={14} /> Exit
            </button>
          )}
        </div>
      </div>

      {/* Main Studio Area */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Left Floating Tools Palette */}
        <div className="w-16 bg-black/60 border-r border-white/10 p-2 flex flex-col items-center gap-2 shrink-0 z-30">
          <button
            onClick={() => setActiveTool('chisel')}
            title="Chisel Nib Brush (Angled Calligraphy)"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'chisel' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Paintbrush size={18} />
          </button>
          
          <button
            onClick={() => setActiveTool('pen')}
            title="Standard Pen Brush"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'pen' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Pencil size={18} />
          </button>

          <button
            onClick={() => setActiveTool('eraser')}
            title="Eraser Tool"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'eraser' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Eraser size={18} />
          </button>

          <button
            onClick={() => setActiveTool('bucket')}
            title="Paint Bucket / Flood Fill"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'bucket' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <PaintBucket size={18} />
          </button>

          <button
            onClick={() => setActiveTool('eyedropper')}
            title="Eyedropper Color Picker"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'eyedropper' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Pipette size={18} />
          </button>

          <button
            onClick={() => setActiveTool('wand')}
            title="Magic Wand Selection"
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeTool === 'wand' ? 'bg-[var(--neon)] text-black font-black shadow-[0_0_15px_var(--neon)]' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'}`}
          >
            <Wand2 size={18} />
          </button>

          <div className="w-8 h-[1px] bg-white/10 my-1" />

          {/* Color Picker Swatch */}
          <div className="relative group">
            <input
              type="color"
              value={brushColor}
              onChange={e => setBrushColor(e.target.value)}
              className="w-10 h-10 rounded-xl border border-white/20 cursor-pointer overflow-hidden opacity-0 absolute inset-0 z-10"
            />
            <div 
              className="w-10 h-10 rounded-xl border-2 border-white/30 shadow-lg flex items-center justify-center transition-transform group-hover:scale-105"
              style={{ backgroundColor: brushColor }}
            />
          </div>
        </div>

        {/* Center Canvas Stage */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#05070d] items-center justify-center p-4 relative overflow-hidden">
          {/* Autosaved snapshots for this channel. Click one to stamp it back
              onto the active layer (undoable). */}
          {showArchive && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 w-[min(560px,92%)] p-3 rounded-2xl bg-black/90 border border-white/15 backdrop-blur-xl shadow-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-white/70 flex items-center gap-1.5">
                  <History size={12} className="text-[var(--neon)]" /> Recent Drawings
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={archiveSnapshot}
                    className="px-2 py-1 rounded-lg bg-[var(--neon)]/10 border border-[var(--neon)]/30 text-[var(--neon)] text-[9px] font-black uppercase tracking-widest hover:bg-[var(--neon)]/20 transition-all"
                  >
                    Save Now
                  </button>
                  <button onClick={() => setShowArchive(false)} className="p-1 text-white/40 hover:text-white transition-colors">
                    <X size={14} />
                  </button>
                </div>
              </div>
              {archive.length === 0 ? (
                <p className="text-[10px] text-white/30 py-4 text-center uppercase tracking-widest">
                  Nothing saved yet — autosaves every 2 min and on exit
                </p>
              ) : (
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  {archive.map(snap => (
                    <button
                      key={snap.at}
                      onClick={() => restoreSnapshot(snap.data)}
                      title={`Restore ${new Date(snap.at).toLocaleString()}`}
                      className="shrink-0 w-32 rounded-xl overflow-hidden border border-white/10 hover:border-[var(--neon)] transition-all group"
                    >
                      <img src={snap.data} alt="" className="w-full aspect-video object-cover" />
                      <div className="px-1.5 py-1 text-[8px] font-mono text-white/40 group-hover:text-[var(--neon)] transition-colors">
                        {new Date(snap.at).toLocaleTimeString()}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Top Brush Adjuster Bar */}
          <div className="mb-2 px-4 py-2 bg-black/60 border border-white/10 rounded-2xl flex items-center gap-6 z-20 shadow-xl backdrop-blur-md">
            {activeTool === 'chisel' && (
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-black uppercase text-white/50 tracking-widest">Nib Angle</span>
                <input
                  type="range"
                  min="0"
                  max="180"
                  value={chiselAngle}
                  onChange={e => setChiselAngle(Number(e.target.value))}
                  className="w-24 accent-[var(--neon)]"
                />
                <span className="text-xs font-mono text-[var(--neon)] w-8 text-right">{chiselAngle}°</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black uppercase text-white/50 tracking-widest">Size</span>
              {/* min was 2px — 1px lets you actually do fine linework, which
                  is the point of the higher zoom ceiling below. */}
              <input
                type="range"
                min="1"
                max="120"
                value={brushSize}
                onChange={e => setBrushSize(Number(e.target.value))}
                className="w-28 accent-[var(--neon)]"
              />
              <span className="text-xs font-mono text-[var(--neon)] w-8 text-right">{brushSize}px</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black uppercase text-white/50 tracking-widest">Opacity</span>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                value={brushOpacity}
                onChange={e => setBrushOpacity(Number(e.target.value))}
                className="w-24 accent-[var(--neon)]"
              />
              <span className="text-xs font-mono text-[var(--neon)] w-10 text-right">{Math.round(brushOpacity * 100)}%</span>
            </div>

            <div className="w-[1px] h-5 bg-white/10" />

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setZoom(z => Math.max(0.25, Math.round((z - 0.25) * 100) / 100))}
                title="Zoom Out"
                className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
              >
                <ZoomOut size={14} />
              </button>
              <button
                onClick={() => setZoom(1)}
                title="Reset Zoom"
                className="text-xs font-mono text-[var(--neon)] w-12 text-center hover:underline"
              >
                {Math.round(zoom * 100)}%
              </button>
              {/* Ceiling raised 3x -> 8x. With the 1920x1080 buffer there
                  are real pixels to inspect at that magnification. */}
              <button
                onClick={() => setZoom(z => Math.min(8, Math.round((z + 0.25) * 100) / 100))}
                title="Zoom In"
                className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
              >
                <ZoomIn size={14} />
              </button>
            </div>
          </div>

          {/* Display Composite Canvas — the outer wrapper scrolls so a
              zoomed-in canvas (transform: scale() only affects paint, not
              layout size) can be panned into view instead of just
              overflowing off-screen. */}
          <div className="flex-1 w-full min-h-0 overflow-auto flex items-center justify-center">
            <div
              className="relative border-2 border-white/10 rounded-2xl shadow-2xl overflow-hidden max-w-full max-h-[82vh] aspect-[16/9] bg-[#090d16] flex items-center justify-center shrink-0"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
            >
              <canvas
                ref={mainCanvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                onMouseDown={handleStartDraw}
                onMouseMove={handleMoveDraw}
                onMouseUp={handleEndDraw}
                onMouseLeave={handleEndDraw}
                onTouchStart={handleStartDraw}
                onTouchMove={handleMoveDraw}
                onTouchEnd={handleEndDraw}
                className="w-full h-full object-contain cursor-crosshair"
              />
            </div>
          </div>
        </div>

        {/* Right Layer Manager Panel */}
        <AnimatePresence>
          {showLayerPanel && (
            <motion.div
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              className="w-64 bg-black/70 border-l border-white/10 p-4 flex flex-col gap-4 shrink-0 z-30 backdrop-blur-xl"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-white font-black text-xs uppercase tracking-widest flex items-center gap-2">
                  <Layers size={14} className="text-[var(--neon)]" /> Photoshop Layers
                </h3>
                <button
                  onClick={addLayer}
                  className="p-1.5 bg-[var(--neon)] text-black rounded-lg hover:scale-105 transition-all shadow-md"
                  title="Add New Layer"
                >
                  <Plus size={14} />
                </button>
              </div>

              {/* Layer Stack */}
              <div className="flex-1 overflow-y-auto space-y-2 pr-1 no-scrollbar">
                {layers.slice().reverse().map(layer => {
                  const isActive = layer.id === activeLayerId;
                  return (
                    <div
                      key={layer.id}
                      onClick={() => setActiveLayerId(layer.id)}
                      className={`p-3 rounded-2xl border transition-all cursor-pointer flex flex-col gap-2 ${isActive ? 'bg-[var(--neon)]/10 border-[var(--neon)]/40 shadow-lg' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-black uppercase tracking-tight ${isActive ? 'text-[var(--neon)]' : 'text-white/80'}`}>
                          {layer.name}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                            className="text-white/40 hover:text-white transition-colors"
                          >
                            {layer.visible ? <Eye size={14} /> : <EyeOff size={14} className="text-red-400" />}
                          </button>
                          {layer.id !== 'bg-layer' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                              className="text-white/40 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Opacity Slider per Layer */}
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span className="text-[8px] font-mono text-white/40">OPACITY</span>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={layer.opacity}
                          onChange={e => updateLayerOpacity(layer.id, Number(e.target.value))}
                          className="flex-1 accent-[var(--neon)]"
                        />
                        <span className="text-[9px] font-mono text-[var(--neon)] w-6 text-right">{Math.round(layer.opacity * 100)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paste Image Hint */}
              <div className="p-3 bg-white/5 border border-white/10 rounded-2xl text-[9px] text-white/40 font-mono flex items-center gap-2">
                <ImageIcon size={14} className="text-[var(--neon)] shrink-0" />
                <span>Tip: Press <kbd className="px-1 py-0.5 bg-black rounded border border-white/20 text-white font-bold">Ctrl+V</kbd> to paste images into layers!</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
